/**
 * SF-4668 — which companies the signed-in staff member may CHANGE.
 *
 * sf-api `GET /admin/v1/me/editable-companies` answers from the caller's company list in the
 * web app (`validCompanies`): `{ all: true }` for staff with no list, `{ all: false, companyIds }`
 * for staff limited to some companies. Viewing is unaffected; this only hides edit controls.
 *
 * The server refuses writes outside the list either way, so this is a convenience. It still fails
 * closed: while loading, or if the lookup fails, nothing is editable.
 */
import { useCustom } from '@refinedev/core';

export const EDITABLE_COMPANIES_URL = '/admin/v1/me/editable-companies';

export interface EditableCompanies {
  all: boolean;
  companyIds: string[];
}

/**
 * The sf-api answer, or null when it is not the documented shape (treated as "nothing editable").
 * `all` must be a real boolean and every id a string.
 */
export function parseEditableCompanies(raw: unknown): EditableCompanies | null {
  if (!raw || typeof raw !== 'object') return null;
  const { all, companyIds } = raw as { all?: unknown; companyIds?: unknown };
  if (all === true) return { all: true, companyIds: [] };
  if (all !== false || !Array.isArray(companyIds)) return null;
  if (!companyIds.every((id) => typeof id === 'string')) return null;
  return { all: false, companyIds: companyIds as string[] };
}

/** May the caller change this company, given the sf-api answer? Ids compare case-insensitively. */
export function canEditCompanyId(raw: unknown, companyId: string | undefined): boolean {
  const scope = parseEditableCompanies(raw);
  if (!scope || !companyId) return false;
  if (scope.all) return true;
  return scope.companyIds.some((id) => id.toLowerCase() === companyId.toLowerCase());
}

/** The caller's editable companies. Cached for the session: the list rarely changes. */
export function useEditableCompanies() {
  return useCustom<EditableCompanies>({
    url: EDITABLE_COMPANIES_URL,
    method: 'get',
    // One retry: a failed lookup leaves everything read-only, so settle quickly rather than spin.
    queryOptions: { staleTime: 60_000, refetchOnWindowFocus: false, retry: 1 },
    errorNotification: false,
  });
}

/**
 * SF-4813: the scope for destructive list actions (Cancel, Retry on Deleted companies). Unlike
 * useCanEditCompany it fails closed on a cached answer too: while the list is being read again,
 * or after a read failed, it answers null (no action), since the person's list may have changed.
 */
export function settledEditScope(query: {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  data?: { data?: unknown };
}): EditableCompanies | null {
  if (query.isLoading || query.isFetching || query.isError) return null;
  return parseEditableCompanies(query.data?.data);
}

/**
 * `{ canEditCompany, isLoading, isError }` for one company. False until a good answer is in. Uses
 * the last good answer if a later background refresh fails, so a network blip does not throw
 * someone off an edit page mid-save (the server checks every save anyway). With no good answer
 * yet, an error leaves everything read-only, and `isError` says the answer never arrived (so
 * "false" means "unknown", not "not on your list"). `refetch` asks again.
 */
export function useCanEditCompany(companyId: string | undefined) {
  const query = useEditableCompanies();
  const scope = query.data?.data;
  return {
    canEditCompany: canEditCompanyId(scope, companyId),
    /** Platform-wide settings: only staff with no company list. */
    canEditPlatform: parseEditableCompanies(scope)?.all === true,
    isLoading: query.isLoading,
    /** The lookup failed and no good answer has ever arrived. */
    isError: query.isError && !query.data,
    refetch: query.refetch,
  };
}
